import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Badge, type BadgeColor, type OUTLINE_COLORS } from '../Badge';
import { Tooltip } from '../Tooltip';

/** How much historical data backs the estimate. Kept local to the library (no app/domain imports). */
export type ResponseConfidence = 'high' | 'established' | 'low';

type ConfidenceBadgeStyle =
  | { kind?: 'status'; color: BadgeColor }
  | { kind: 'outline'; color: (typeof OUTLINE_COLORS)[number] };

// Confidence tier → badge style, matching the design tokens: High solid blue (blue-20), Established
// solid grey (grey-30), Low a light grey-10 fill with a grey-20 border. Unknown (null) falls back to
// the neutral grey badge (handled at the call site below).
const CONFIDENCE_BADGE_STYLE: Record<ResponseConfidence, ConfidenceBadgeStyle> = {
  high: { color: 'blue' },
  established: { color: 'grey' },
  low: { kind: 'outline', color: 'grey' },
};

/** User-facing tier names, used by the hover descriptions and any copy that names a tier. */
export const CONFIDENCE_TIER_LABELS: Record<ResponseConfidence, string> = {
  high: 'High',
  established: 'Established',
  low: 'Low',
};

/** What each tier means, as a sentence fragment that follows the tier name. */
export const CONFIDENCE_TIER_DETAILS: Record<ResponseConfidence, string> = {
  high: 'sufficient recent requests to trust the range from this agency.',
  established: 'some recent history with this agency.',
  low: 'limited recent data; treat the range as a rough guide.',
};

// Hover copy for each tier. Single source of truth for the confidence descriptions.
export const CONFIDENCE_TIER_DESCRIPTIONS: Record<ResponseConfidence, string> = {
  high: `${CONFIDENCE_TIER_LABELS.high} Confidence — ${CONFIDENCE_TIER_DETAILS.high}`,
  // "Established" carries no "Confidence" suffix, unlike the other two tiers.
  established: `${CONFIDENCE_TIER_LABELS.established} — ${CONFIDENCE_TIER_DETAILS.established}`,
  low: `${CONFIDENCE_TIER_LABELS.low} Confidence — ${CONFIDENCE_TIER_DETAILS.low}`,
};

interface ConfidenceEstimateProps extends Omit<React.ComponentProps<typeof Badge>, 'kind' | 'color' | 'children'> {
  /** The estimate text shown inside the pill, e.g. "3–5 weeks". */
  label: string;
  /** Confidence tier driving the pill colour. `null` (unknown) falls back to the neutral grey chip. */
  confidence: ResponseConfidence | null;
  /**
   * When true, hovering the pill shows the tier's confidence description (see CONFIDENCE_TIER_DESCRIPTIONS).
   * No-op for unknown (`null`) confidence, which has no description. The in-table pills opt into this: the
   * hover is the only place a tier is explained.
   */
  showTooltip?: boolean;
}

/** Renders an estimated-response-time value as a coloured pill, coloured by confidence tier. */
export function ConfidenceEstimate({ label, confidence, showTooltip, className, ...props }: ConfidenceEstimateProps) {
  const hasTooltip = !!showTooltip && !!confidence;
  const badgeStyle: ConfidenceBadgeStyle = confidence ? CONFIDENCE_BADGE_STYLE[confidence] : { color: 'grey' };
  const chip = (
    <Badge
      data-slot="confidence-estimate"
      {...badgeStyle}
      // The pill is a label, not selectable text: keep the arrow cursor, or the help cursor when it
      // carries a hover explanation.
      className={cn(hasTooltip ? 'cursor-help' : 'cursor-default', className)}
      {...props}
    >
      {label}
    </Badge>
  );

  if (!hasTooltip) return chip;

  return (
    <Tooltip text={CONFIDENCE_TIER_DESCRIPTIONS[confidence]} side="top" isArrowVisible className="max-w-60">
      {chip}
    </Tooltip>
  );
}
